import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { AlertTriangle, ArrowLeft, CheckCircle2, ExternalLink, History, RefreshCw, Save, Unplug, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NlDateInput } from '@/components/ui/nl-date-input';
import { Separator } from '@/components/ui/separator';
import { api } from '@/lib/api';
import { disconnectExact, historicalImportExact, putExactEntityDefaults, syncExact } from '@/lib/mutations';
import { formatDateTimeISO } from '@/lib/utils';

export const Route = createFileRoute('/admin/entities_/$code/exact')({
	component: EntityExactPage,
});

function EntityExactPage() {
	const { code } = Route.useParams();
	const queryClient = useQueryClient();
	const meQuery = useQuery({ queryKey: ['me'], queryFn: () => api.me() });
	const credQuery = useQuery({
		queryKey: ['exact-credentials', code],
		queryFn: () => api.exactCredentials(code),
		enabled: meQuery.data?.role === 'admin',
		// Lichte refetch zodat na een redirect-callback de status update.
		refetchOnMount: 'always',
	});

	const [syncError, setSyncError] = useState<string | null>(null);
	const [syncResult, setSyncResult] = useState<string | null>(null);

	const [connectError, setConnectError] = useState<string | null>(null);

	const connectMutation = useMutation({
		mutationFn: () => api.exactAuthUrl(code),
		onSuccess: (res) => {
			setConnectError(null);
			window.location.href = res.url;
		},
		onError: (err: unknown) => {
			const body = err && typeof err === 'object' && 'body' in err ? (err as { body: unknown }).body : err;
			setConnectError(typeof body === 'object' ? JSON.stringify(body) : String(body));
		},
	});

	const syncMutation = useMutation({
		mutationFn: () => syncExact(code),
		onSuccess: (res) => {
			setSyncError(null);
			setSyncResult(
				`Bijgewerkt: ${res.updated.creditors} crediteuren, ${res.updated.ledgers} grootboekrekeningen, ${res.updated.vatCodes} BTW-codes, ${res.updated.journals} dagboeken, ${res.updated.paymentStatusUpdated} betaalstatus-wijzigingen.`,
			);
			queryClient.invalidateQueries({ queryKey: ['exact-credentials', code] });
			queryClient.invalidateQueries({ queryKey: ['exact-journals', code] });
		},
		onError: (err: unknown) => {
			const body = err && typeof err === 'object' && 'body' in err ? (err as { body: unknown }).body : err;
			setSyncError(typeof body === 'object' ? JSON.stringify(body) : String(body));
			setSyncResult(null);
		},
	});

	const disconnectMutation = useMutation({
		mutationFn: () => disconnectExact(code),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ['exact-credentials', code] }),
	});

	if (meQuery.isLoading) return <div className="p-8 text-sm text-muted-foreground">Laden…</div>;
	if (meQuery.data?.role !== 'admin') {
		return (
			<div className="mx-auto max-w-md p-8 text-center text-sm">
				<AlertTriangle className="mx-auto mb-2 h-6 w-6 text-destructive" />
				Alleen admins kunnen Exact-koppelingen beheren.
			</div>
		);
	}

	const data = credQuery.data;
	const connected = data?.connected ?? false;

	return (
		<div className="mx-auto max-w-4xl space-y-6">
			<div className="flex items-center gap-3">
				<Button variant="ghost" size="sm" asChild>
					<Link to="/admin/entities">
						<ArrowLeft className="mr-1.5 h-4 w-4" />
						Terug
					</Link>
				</Button>
				<div>
					<h1 className="text-2xl font-semibold tracking-tight">Exact Online — {code}</h1>
					<p className="text-sm text-muted-foreground">
						Koppel deze entiteit aan een Exact-administratie zodat inkoopfacturen direct als concept geboekt kunnen
						worden.
					</p>
				</div>
			</div>

			<Card>
				<CardHeader className="pb-3">
					<CardTitle className="text-sm font-medium">Verbindingsstatus</CardTitle>
				</CardHeader>
				<CardContent className="space-y-3 text-sm">
					{credQuery.isLoading && <p className="text-muted-foreground">Laden…</p>}
					{credQuery.isError && (
						<p className="text-xs text-destructive">
							Status ophalen mislukt:{' '}
							{credQuery.error instanceof Error ? credQuery.error.message : String(credQuery.error)}
						</p>
					)}
					{!credQuery.isLoading && !credQuery.isError && !connected && (
						<>
							<div className="flex items-center gap-2 text-muted-foreground">
								<Unplug className="h-4 w-4" />
								Nog niet verbonden met Exact Online.
							</div>
							<Button
								size="sm"
								onClick={() => connectMutation.mutate()}
								disabled={connectMutation.isPending}
							>
								<ExternalLink className="mr-1.5 h-4 w-4" />
								{connectMutation.isPending ? 'Doorverwijzen…' : 'Verbinden met Exact Online'}
							</Button>
							{connectError && <p className="text-xs text-destructive">{connectError}</p>}
							<p className="text-xs text-muted-foreground">
								Je wordt doorgestuurd naar Exact Online voor consent. Na "Toestaan" kom je hier weer terug.
							</p>
						</>
					)}
					{!credQuery.isLoading && connected && data?.credentials && (
						<>
							<div className="flex items-center gap-2 text-emerald-700">
								<CheckCircle2 className="h-4 w-4" />
								Verbonden met administratie <Badge variant="muted">{data.credentials.division_id}</Badge>
							</div>
							<div className="grid grid-cols-1 gap-2 text-xs text-muted-foreground sm:grid-cols-2">
								<div>
									<dt>Gekoppeld op</dt>
									<dd className="font-mono text-foreground">{formatDateTimeISO(data.credentials.connected_at)}</dd>
								</div>
								<div>
									<dt>Token verloopt</dt>
									<dd className="font-mono text-foreground">{formatDateTimeISO(data.credentials.access_token_expires_at)}</dd>
								</div>
							</div>
							<Separator />
							<div className="flex flex-wrap gap-2">
								<Button
									size="sm"
									variant="outline"
									onClick={() => syncMutation.mutate()}
									disabled={syncMutation.isPending}
								>
									<RefreshCw className={syncMutation.isPending ? 'mr-1.5 h-4 w-4 animate-spin' : 'mr-1.5 h-4 w-4'} />
									{syncMutation.isPending ? 'Bezig…' : 'Stamdata bijwerken'}
								</Button>
								<Button
									size="sm"
									variant="outline"
									className="text-destructive hover:bg-destructive/10 hover:text-destructive"
									onClick={() => {
										if (window.confirm('Verbinding met Exact verbreken?')) disconnectMutation.mutate();
									}}
									disabled={disconnectMutation.isPending}
								>
									<Unplug className="mr-1.5 h-4 w-4" />
									Verbreken
								</Button>
							</div>
							{syncResult && <p className="text-xs text-emerald-700">{syncResult}</p>}
							{syncError && <p className="text-xs text-destructive">{syncError}</p>}
						</>
					)}
				</CardContent>
			</Card>

			{connected && data?.cache_counts && (
				<Card>
					<CardHeader className="pb-3">
						<CardTitle className="text-sm font-medium">Stamdata-cache</CardTitle>
					</CardHeader>
					<CardContent className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
						<CacheBlock label="Crediteuren" count={data.cache_counts.creditors} synced={data.sync?.last_creditors_sync} />
						<CacheBlock label="Grootboekrekeningen" count={data.cache_counts.ledgers} synced={data.sync?.last_ledgers_sync} />
						<CacheBlock label="BTW-codes" count={data.cache_counts.vat_codes} synced={data.sync?.last_vat_codes_sync} />
						<CacheBlock label="Dagboeken" count={data.cache_counts.journals} synced={data.sync?.last_journals_sync} />
					</CardContent>
				</Card>
			)}

			{connected && <EntityDefaultsCard entityCode={code} />}
			{connected && <HistoricalImportCard entityCode={code} />}
		</div>
	);
}

function EntityDefaultsCard({ entityCode }: { entityCode: string }) {
	const queryClient = useQueryClient();
	const defaultsQuery = useQuery({
		queryKey: ['exact-entity-defaults', entityCode],
		queryFn: () => api.exactEntityDefaults(entityCode),
	});
	const journalsQuery = useQuery({
		queryKey: ['exact-journals', entityCode],
		queryFn: () => api.exactJournals(entityCode),
	});
	const ledgersQuery = useQuery({
		queryKey: ['exact-ledgers', entityCode],
		queryFn: () => api.exactLedgers(entityCode),
	});

	const [journal, setJournal] = useState<string>('');
	const [purchaseLedger, setPurchaseLedger] = useState<string>('');
	const [shippingLedger, setShippingLedger] = useState<string>('');
	const [isVatExempt, setIsVatExempt] = useState<boolean>(false);
	const [savedAt, setSavedAt] = useState<string | null>(null);
	const [saveError, setSaveError] = useState<string | null>(null);
	const [savedResetCount, setSavedResetCount] = useState<number | null>(null);

	useEffect(() => {
		const d = defaultsQuery.data?.defaults;
		if (d) {
			setJournal(d.default_journal ?? '');
			setPurchaseLedger(d.default_purchase_ledger ?? '');
			setShippingLedger(d.default_shipping_ledger ?? '');
			setIsVatExempt(d.is_vat_exempt ?? false);
		}
	}, [defaultsQuery.data]);

	const saveMutation = useMutation({
		mutationFn: () =>
			putExactEntityDefaults(entityCode, {
				default_journal: journal === '' ? null : journal,
				default_purchase_ledger: purchaseLedger === '' ? null : purchaseLedger,
				default_shipping_ledger: shippingLedger === '' ? null : shippingLedger,
				is_vat_exempt: isVatExempt,
			}),
		onSuccess: (res) => {
			setSaveError(null);
			setSavedAt(new Date().toISOString());
			setSavedResetCount(res.proposal_reset_count ?? 0);
			queryClient.invalidateQueries({ queryKey: ['exact-entity-defaults', entityCode] });
			queryClient.invalidateQueries({ queryKey: ['inkoopfactuur'] });
		},
		onError: (err: unknown) => {
			const body = err && typeof err === 'object' && 'body' in err ? (err as { body: unknown }).body : err;
			setSaveError(typeof body === 'object' ? JSON.stringify(body) : String(body));
		},
	});

	// Exact Journal.Type 22 = Inkoop (empirisch). PurchaseEntries horen alleen
	// in een inkoopboek; memoriaal/bank/verkoop sluiten we uit.
	const inkoopboeken = (journalsQuery.data?.items ?? []).filter((j) => j.type === '22');
	const ledgers = ledgersQuery.data?.items ?? [];

	return (
		<Card>
			<CardHeader className="pb-3">
				<CardTitle className="text-sm font-medium">Standaardinstellingen voor deze entiteit</CardTitle>
			</CardHeader>
			<CardContent className="space-y-4 text-sm">
				<p className="text-xs text-muted-foreground">
					Worden gebruikt als fallback wanneer een crediteur geen eigen voorkeuren heeft.
				</p>

				<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
					<label className="space-y-1">
						<span className="text-xs font-medium">Inkoopboek</span>
						<select
							className="block w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm"
							value={journal}
							onChange={(e) => setJournal(e.target.value)}
						>
							<option value="">— niet ingesteld —</option>
							{inkoopboeken.map((j) => (
								<option key={j.exact_id} value={j.code}>
									{j.code} — {j.description}
								</option>
							))}
						</select>
						{inkoopboeken.length === 0 && journalsQuery.isFetched && (
							<span className="text-[11px] text-muted-foreground">
								Geen inkoopboeken in cache. Klik "Stamdata bijwerken".
							</span>
						)}
					</label>

					<div />

					<label className="space-y-1">
						<span className="text-xs font-medium">Default grootboekrekening (inkoop)</span>
						<select
							className="block w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm"
							value={purchaseLedger}
							onChange={(e) => setPurchaseLedger(e.target.value)}
						>
							<option value="">— niet ingesteld —</option>
							{ledgers.map((l) => (
								<option key={l.exact_id} value={l.code}>
									{l.code} — {l.description}
								</option>
							))}
						</select>
					</label>

					<label className="space-y-1">
						<span className="text-xs font-medium">Verzendkosten-grootboek</span>
						<select
							className="block w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm"
							value={shippingLedger}
							onChange={(e) => setShippingLedger(e.target.value)}
						>
							<option value="">— niet ingesteld —</option>
							{ledgers.map((l) => (
								<option key={l.exact_id} value={l.code}>
									{l.code} — {l.description}
								</option>
							))}
						</select>
					</label>
				</div>

				<div className="rounded-md border bg-muted/30 p-3">
					<label className="flex items-start gap-2">
						<input
							type="checkbox"
							className="mt-0.5 h-4 w-4 rounded border-input"
							checked={isVatExempt}
							onChange={(e) => setIsVatExempt(e.target.checked)}
						/>
						<span className="space-y-0.5">
							<span className="block text-xs font-medium">Niet BTW-plichtig</span>
							<span className="block text-[11px] text-muted-foreground">
								Inkoopfacturen worden inclusief BTW geboekt op het grootboek. Geen BTW-code of BTW-bedrag wordt naar
								Exact verstuurd. Wijziging triggert het opnieuw genereren van alle openstaande boekingsvoorstellen
								van deze entiteit.
							</span>
						</span>
					</label>
				</div>

				<div className="flex items-center gap-3">
					<Button size="sm" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
						<Save className="mr-1.5 h-4 w-4" />
						{saveMutation.isPending ? 'Opslaan…' : 'Opslaan'}
					</Button>
					{savedAt && !saveMutation.isPending && (
						<span className="text-xs text-emerald-700">
							Opgeslagen om {formatDateTimeISO(savedAt)}
							{savedResetCount !== null && savedResetCount > 0
								? ` — ${savedResetCount} boekingsvoorstel${savedResetCount === 1 ? '' : 'len'} gereset`
								: ''}
						</span>
					)}
					{saveError && <span className="text-xs text-destructive">{saveError}</span>}
				</div>
			</CardContent>
		</Card>
	);
}

function CacheBlock({ label, count, synced }: { label: string; count: number; synced: string | null | undefined }) {
	return (
		<div className="rounded-md border p-3">
			<div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
			<div className="mt-1 text-xl font-semibold">{count}</div>
			<div className="mt-1 text-[11px] text-muted-foreground">
				{synced ? `bijgewerkt ${formatDateTimeISO(synced)}` : 'nog niet gesynchroniseerd'}
			</div>
		</div>
	);
}

interface HistoricalBatchResult {
	processed: number;
	skipped_duplicate: number;
	merged_with_existing: number;
	skipped_no_attachment: number;
	more_available: boolean;
	next_skip: number;
	last_entry_date: string | null;
}

function HistoricalImportCard({ entityCode }: { entityCode: string }) {
	const [since, setSince] = useState('2025-01-01');
	const [until, setUntil] = useState('');
	const [maxPerBatch, setMaxPerBatch] = useState(15);
	const [history, setHistory] = useState<HistoricalBatchResult[]>([]);
	const [skip, setSkip] = useState<number>(0);
	const [error, setError] = useState<string | null>(null);

	const totals = history.reduce(
		(acc, b) => ({
			processed: acc.processed + b.processed,
			skipped_duplicate: acc.skipped_duplicate + b.skipped_duplicate,
			merged_with_existing: acc.merged_with_existing + b.merged_with_existing,
			skipped_no_attachment: acc.skipped_no_attachment + b.skipped_no_attachment,
		}),
		{ processed: 0, skipped_duplicate: 0, merged_with_existing: 0, skipped_no_attachment: 0 },
	);
	const lastBatch = history[history.length - 1];
	const inProgress = history.length > 0 && lastBatch?.more_available === true;

	const mutation = useMutation({
		mutationFn: () => {
			if (!/^\d{4}-\d{2}-\d{2}$/.test(since)) throw new Error('Vul een geldige startdatum in (YYYY-MM-DD).');
			const body: { since: string; until?: string; max?: number; skip?: number } = {
				since,
				max: maxPerBatch,
			};
			if (until) body.until = until;
			body.skip = skip;
			return historicalImportExact(entityCode, body);
		},
		onSuccess: (res) => {
			setError(null);
			setHistory((h) => [...h, res]);
			setSkip(res.next_skip);
		},
		onError: (err) => {
			const body = err && typeof err === 'object' && 'body' in err ? (err as { body: unknown }).body : err;
			setError(typeof body === 'object' ? JSON.stringify(body) : String(body));
		},
	});

	const reset = () => {
		setHistory([]);
		setSkip(0);
		setError(null);
	};

	return (
		<Card>
			<CardHeader className="pb-3">
				<CardTitle className="text-sm font-medium">
					<History className="mr-1.5 inline h-4 w-4 align-text-bottom" />
					Historische facturen importeren
				</CardTitle>
			</CardHeader>
			<CardContent className="space-y-3 text-sm">
				<p className="text-xs text-muted-foreground">
					Importeert geboekte inkoopfacturen uit Exact als <code>source=exact_historical</code>, target=general.
					Worden niet opnieuw geboekt — alleen gearchiveerd in DocR voor zoek + audit. PDF + header-totalen + regels (incl.
					grootboek + BTW-code) worden direct uit Exact overgenomen; OCR + embedding draaien daarna in batch-mode (50%
					goedkoper, classify wordt overgeslagen omdat alle classify-data al uit Exact komt).
				</p>
				<div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
					<div>
						<Label className="text-xs">Vanaf</Label>
						<NlDateInput
							value={since}
							onChange={(iso) => {
								setSince(iso);
								reset();
							}}
							disabled={mutation.isPending || inProgress}
						/>
					</div>
					<div>
						<Label className="text-xs">Tot (optioneel)</Label>
						<NlDateInput
							value={until}
							onChange={(iso) => {
								setUntil(iso);
								reset();
							}}
							placeholder="leeg = vandaag"
							disabled={mutation.isPending || inProgress}
						/>
					</div>
					<div>
						<Label className="text-xs">Max per batch</Label>
						<Input
							type="number"
							min={1}
							max={50}
							value={maxPerBatch}
							onChange={(e) => setMaxPerBatch(Number(e.target.value) || 15)}
							disabled={mutation.isPending}
						/>
					</div>
				</div>
				{error && (
					<div className="flex items-center gap-2 rounded border border-destructive/40 bg-destructive/5 px-2 py-1.5 text-xs text-destructive">
						<XCircle className="h-3.5 w-3.5" />
						{error}
					</div>
				)}
				{history.length > 0 && (
					<div className="space-y-1 rounded border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
						<div>
							{history.length} batch{history.length === 1 ? '' : 'es'} verwerkt — totaal{' '}
							<span className="font-medium text-foreground">{totals.processed}</span> nieuwe documents,{' '}
							{totals.merged_with_existing} verrijkt, {totals.skipped_duplicate} overgeslagen (al bekend),{' '}
							{totals.skipped_no_attachment} zonder PDF.
						</div>
						{lastBatch?.last_entry_date && (
							<div>
								Laatste verwerkte factuurdatum: <span className="font-mono">{lastBatch.last_entry_date}</span>
							</div>
						)}
						{lastBatch?.more_available ? (
							<div className="text-amber-700">Er zijn nog meer facturen — klik &quot;Volgende batch&quot;.</div>
						) : (
							<div className="text-emerald-700">Klaar voor deze datum-range.</div>
						)}
					</div>
				)}
				<div className="flex justify-end gap-2">
					{inProgress && (
						<Button size="sm" variant="outline" onClick={reset}>
							Annuleren
						</Button>
					)}
					<Button
						size="sm"
						onClick={() => mutation.mutate()}
						disabled={mutation.isPending || !since || (history.length > 0 && !inProgress)}
					>
						{mutation.isPending ? 'Bezig…' : inProgress ? 'Volgende batch' : 'Start import'}
					</Button>
				</div>
			</CardContent>
		</Card>
	);
}
